import { ArrowRightIcon, ChevronDown, ChevronUp, SendIcon } from "lucide-react";
import React, { useMemo, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fromJS } from "immutable";
import CollapsibleCard from "../../../components/ui/CollapsibleCard";
import { formatDistanceToNow } from "date-fns";
import { SmilePlus, AtSign, CheckCircle, Reply, Trash2, Circle } from "lucide-react";
import {
  createItemLogActivity,
  deleteItemLog,
  markAsDoneActivity,
  updateTaskField,
} from "../../../api/ProjectApi";
import LayoutAction from "../../../actions/LayoutAction";
import { commonEmojis } from "../../../constants/commonEmojis";
import ActivityAction from "../../../actions/ActivityAction";
import ProjectAction from "../../../actions/ProjectAction";
import { ActivityTypes } from "../../../constants/action-types";
import CommentInput from "../../../components/CommentInput";
import DefaultAvatar from "../../../components/DefaultAvatar";
import ConfirmDialog from "../../../dialog/ConfirmDialog";
import OctagonAlertIcon from "../../../icons/OctagonAlertIcon";
import { base64ToBlob } from "../../../components/quill-mention/utils";
import { getUserTimeZone, isGmailContext } from "../../../api/api";

interface CommentReaction {
  emoji: string;
  count: number;
  userReacted: boolean; // Whether current user has reacted
}

interface CommentItem {
  id: string;
  author?: string;
  authorId?: string;
  avatarUrl?: string;
  createdAt?: number; // seconds since epoch
  content?: string;
  replies?: number;
  parentId?: string; // ID of parent comment if this is a reply
  isResolved?: boolean;
  resolvedBy?: string;
  reactions?: CommentReaction[];
  children?: CommentItem[]; // Nested replies
}

interface CommentsPanelProps {
  itemId: string;
  itemType: string;
  itemLabel?: string;
  itemActivities: any; // Immutable List
  isLoading: boolean;
}

// Format comment content to ensure mentions have "@" prefix (but avoid double "@@")
const formatCommentContent = (content: string): string => {
  if (!content) return "";

  // Handle newlines first
  let html = content.replace(/\n/g, "<br />");

  // Find mention spans with nested structure and ensure they have "@" prefix
  // Pattern: <span class="mention" ...>...</span> or <span class="mention2" ...>...</span>
  // Can have deeply nested spans like: <span class="mention"><span><span>Name</span></span></span>
  // Use a more robust regex that matches the entire mention span including all nested content
  html = html.replace(
    /<span\s+class=["']mention(?:2)?["'][^>]*>([\s\S]*?)<\/span>/gi,
    (match, innerContent) => {
      // Extract all text content from the ENTIRE match (not just innerContent) to check for "@"
      const fullMatchText = match.replace(/<[^>]+>/g, "").trim();
      const innerText = innerContent.replace(/<[^>]+>/g, "").trim();

      // Check if the entire match text already starts with "@" (including double "@@")
      // This is the most reliable check - if the final rendered text has "@", don't modify
      // Also check if innerContent HTML already contains "@" (to catch cases where "@" is in HTML but not in textContent)
      if (fullMatchText.startsWith("@") || fullMatchText.match(/^@+/)) {
        // Already has "@" at the start (even if it's "@@"), return as-is
        // Don't modify mentions that already have "@" (including "@@")
        return match;
      }

      // Also check if innerContent HTML already contains "@" anywhere (to catch cases where "@" is in HTML structure)
      if (innerContent.includes("@")) {
        // "@" is already present in the HTML structure, return as-is
        return match;
      }

      // For Quill mention structure: <span class="mention"><span contenteditable="false"><span class="ql-mention-denotation-char"></span><span>Name</span></span></span>
      // Find all spans and check which one contains the name text
      // Look for span that is NOT ql-mention-denotation-char and contains text
      const allSpanMatches = [...innerContent.matchAll(/<span[^>]*>([^<]*)<\/span>/g)];

      // Find the span that contains the actual name (not ql-mention-denotation-char)
      let nameSpanMatch = null;
      let nameText = "";

      for (const spanMatch of allSpanMatches) {
        const spanContent = spanMatch[1].trim();
        // Skip empty spans and ql-mention-denotation-char spans
        if (spanContent && !spanMatch[0].includes("ql-mention-denotation-char")) {
          nameSpanMatch = spanMatch;
          nameText = spanContent;
          break;
        }
      }

      if (nameSpanMatch && nameText) {
        // Check if nameText already starts with "@" (avoid double "@@")
        if (nameText.startsWith("@") || nameText.match(/^@+/)) {
          // Already has "@", return match as-is
          return match;
        }
        // Add "@" prefix only if it doesn't already have one
        return match.replace(
          nameSpanMatch[0],
          `<span${nameSpanMatch[0].match(/<span([^>]*)>/)?.[1] || ""}>@${nameText}</span>`
        );
      } else {
        // No name span found, check if innerContent text already starts with "@"
        if (innerText.startsWith("@") || innerText.match(/^@+/)) {
          // Already has "@", return match as-is
          return match;
        }
        // If innerText is empty but we have innerContent, don't add "@"
        // This might be a malformed mention or already formatted mention
        // Return match as-is to avoid adding "@" to empty/invalid mentions
        if (innerText === "" && innerContent) {
          return match;
        }
        // Add "@" at the beginning of innerContent only if it doesn't have "@"
        return match.replace(innerContent, `@${innerContent}`);
      }
    }
  );

  // Handle image
  html = html.replace(/<img[^>]*src="([^"]+)"[^>]*>/g, (match, src) => {
    if (src.startsWith("data:image")) {
      const blob = base64ToBlob(src);
      const url = URL.createObjectURL(blob);
      return `
          <a href="${url}" target="_blank" rel="noopener noreferrer">
            ${match}
          </a>
        `;
    }
    return `
        <a href="${src}" target="_blank" rel="noopener noreferrer">
          ${match}
        </a>
      `;
  });
  return html;
};

// Show more / less for long comment content (>5 lines)
const CommentContent = ({ comment, t }: { comment: CommentItem; t: any }) => {
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = contentRef.current;
    if (el) {
      // Check if content is actually taller than 5-line clamp height
      setIsTruncated(el.scrollHeight > el.clientHeight + 2);
    }
  }, [comment.content]);

  return (
    <div className="mt-1">
      <div
        ref={contentRef}
        className={`text-sm leading-5 break-words overflow-wrap-anywhere max-w-full ${
          comment.isResolved ? "text-gray-500" : "text-gray-900"
        } ${!expanded ? "line-clamp-5" : ""}`}
        style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
        dangerouslySetInnerHTML={{
          __html: formatCommentContent(comment.content || ""),
        }}
      />
      {(isTruncated || expanded) && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          className="mt-0.5 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors cursor-pointer block"
        >
          {expanded ? t("Show less") : t("Show more")}
        </button>
      )}
    </div>
  );
};

const CommentThread = ({
  comment,
  isLast: _isLast,
  isReply = false,
  onDelete,
  onReply,
  onResolve,
  onInsertEmoji,
  onToggleReaction,
  onAddReaction,
  onMention,
  onShowReplies,
  showResolved = true,
}: {
  comment: CommentItem;
  isLast: boolean;
  isReply?: boolean;
  onDelete: (id: string) => void;
  onReply?: (comment: CommentItem) => void;
  onResolve?: (comment: CommentItem, resolved: boolean) => void;
  onInsertEmoji?: (comment: CommentItem, buttonRef: React.RefObject<HTMLButtonElement>) => void;
  onToggleReaction?: (commentId: string, emoji: string) => void;
  onAddReaction?: (commentId: string, buttonRef: React.RefObject<HTMLButtonElement>) => void;
  onMention?: (comment: CommentItem) => void;
  onShowReplies?: (commentId: string) => void;
  showResolved?: boolean;
}) => {
  const { t } = useTranslation();
  const emojiButtonRef = useRef<HTMLButtonElement | null>(null);
  const reactionEmojiButtonRef = useRef<HTMLButtonElement | null>(null);
  const hasReplies = comment.children && comment.children.length > 0;
  const repliesCount = comment.children?.length || comment.replies || 0;

  return (
    <div className={isReply ? "ml-8 mt-3" : ""}>
      {/* Resolved header for resolved comments - shown at top */}
      {comment.isResolved && (
        <div className="flex items-center gap-1.5 mb-2">
          <CheckCircle className="w-4 h-4 text-gray-500" />
          <span className="text-[10px] leading-4 text-gray-500">{t("Resolved by you")}</span>
        </div>
      )}

      {/* Main comment content */}
      <div className="relative group flex gap-3">
        {/* Avatar and vertical line container */}
        <div className="flex-shrink-0 flex flex-col items-center">
          {/* Avatar */}
          <div>
            {comment.avatarUrl ? (
              <img
                src={String(comment.avatarUrl)}
                alt={comment.author}
                className="h-5 w-5 rounded-full object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <DefaultAvatar
                name={comment.author || ""}
                size="sm"
                className="h-5 w-5 rounded-full bg-gray-300 text-white"
              />
            )}
          </div>
          {/* Vertical line - only show when there are replies */}
          {hasReplies && <div className="w-px bg-gray-200 flex-1 mt-1" />}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Author and time */}
          <div className="flex items-center gap-2">
            <span
              className={`text-sm font-medium leading-5 ${
                comment.isResolved ? "text-gray-500" : "text-gray-900"
              }`}
            >
              {comment.author}
            </span>
            <span className="text-xs leading-4 text-gray-500">
              {comment.createdAt
                ? formatDistanceToNow(new Date(comment.createdAt * 1000), {
                    addSuffix: true,
                  })
                : ""}
            </span>
          </div>

          {/* Comment text with Show more */}
          <CommentContent comment={comment} t={t} />

          {/* Reactions, Action icons, and Replies Count */}
          <div className="mt-2 flex items-center gap-2">
            {/* Reactions */}
            {comment.reactions && comment.reactions.length > 0 && (
              <div className="flex items-center gap-1">
                {comment.reactions.map((reaction) => (
                  <button
                    key={reaction.emoji}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleReaction?.(comment.id, reaction.emoji);
                    }}
                    className={`inline-flex items-center gap-0.5 border rounded-md px-1.5 py-0.5 transition-colors ${
                      reaction.userReacted
                        ? "bg-blue-100 border-blue-300"
                        : "bg-blue-50 border-blue-200 hover:bg-blue-100"
                    }`}
                    title={reaction.userReacted ? t("Remove reaction") : t("Add reaction")}
                  >
                    <span className="text-[10px] leading-4">{reaction.emoji}</span>
                    <span className="text-[10px] font-medium leading-4 text-gray-600">
                      {reaction.count}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {/* Action icons - always show, not just when reactions exist */}
            <div className="flex items-center gap-2">
              {onAddReaction && (
                <button
                  ref={reactionEmojiButtonRef}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddReaction(comment.id, reactionEmojiButtonRef);
                  }}
                  className="p-0.5 hover:opacity-70 transition-opacity"
                  title={t("Add reaction") as string}
                >
                  <SmilePlus className="w-3 h-3 text-gray-500" />
                </button>
              )}
              {onMention && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onMention(comment);
                  }}
                  className="p-0.5 hover:opacity-70 transition-opacity"
                  title={t("Mention with @") as string}
                >
                  <AtSign className="w-3 h-3 text-gray-500" />
                </button>
              )}
            </div>
            {repliesCount > 0 && onShowReplies && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onShowReplies(comment.id);
                }}
                className="text-[12px] leading-4 font-normal text-gray-500 hover:underline"
              >
                {repliesCount} {t("replies")}
              </button>
            )}
          </div>
        </div>

        {/* Hover Actions Menu */}
        <div className="absolute top-0 right-0 flex items-center gap-1 px-1.5 py-1 bg-white border border-gray-200 rounded-md shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
          {onReply && (
            <button
              className="p-1 rounded hover:bg-gray-100"
              title={t("Reply") as string}
              onClick={(e) => {
                e.stopPropagation();
                onReply(comment);
              }}
            >
              <Reply className="w-3 h-3 text-gray-500" />
            </button>
          )}
          {onInsertEmoji && (
            <button
              ref={emojiButtonRef}
              className="p-1 rounded hover:bg-gray-100"
              title={t("Insert emoji") as string}
              onClick={(e) => {
                e.stopPropagation();
                onInsertEmoji(comment, emojiButtonRef);
              }}
            >
              <SmilePlus className="w-3 h-3 text-gray-500" />
            </button>
          )}
          {onResolve && (
            <button
              className="p-1 rounded hover:bg-gray-100"
              title={comment.isResolved ? (t("Unresolve") as string) : (t("Resolve") as string)}
              onClick={(e) => {
                e.stopPropagation();
                onResolve(comment, !comment.isResolved);
              }}
            >
              {comment.isResolved ? (
                <Circle className="w-3 h-3 text-gray-500" />
              ) : (
                <CheckCircle className="w-3 h-3 text-gray-500" />
              )}
            </button>
          )}
          <button
            className="p-1 rounded hover:bg-gray-100"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(comment.id);
            }}
            title={t("Delete comment?") as string}
          >
            <Trash2 className="w-3 h-3 text-red-500" />
          </button>
        </div>
      </div>

      {/* Render replies */}
      {hasReplies && (
        <div className="mt-2">
          {comment
            .children!.filter((reply) => showResolved || !reply.isResolved)
            .map((reply, index) => {
              const filteredReplies = comment.children!.filter(
                (r) => showResolved || !r.isResolved
              );
              return (
                <CommentThread
                  key={reply.id}
                  comment={reply}
                  isLast={index === filteredReplies.length - 1}
                  isReply={true}
                  showResolved={showResolved}
                  onDelete={onDelete}
                  onReply={onReply}
                  onResolve={onResolve}
                  onInsertEmoji={onInsertEmoji}
                  onToggleReaction={onToggleReaction}
                  onAddReaction={onAddReaction}
                  onMention={onMention}
                  onShowReplies={onShowReplies}
                />
              );
            })}
        </div>
      )}
    </div>
  );
};

const CommentsPanel: React.FC<CommentsPanelProps> = ({
  itemId,
  itemType,
  itemLabel = "",
  itemActivities,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const currentAvatarUrl = currentUser?.get?.("avatarUrl") || "";
  const projectId = useSelector((state: any) => state.projectReducer.getIn(["project", "id"]));
  const members = useSelector(
    (state: any) => state.projectReducer.getIn(["project", "members"]) || fromJS([])
  );
  const upcomingFilters =
    useSelector((state: any) => state.activityReducer.get("upcomingFilters")) || fromJS({});

  React.useEffect(() => {
    try {
      const size =
        typeof (members as any)?.size === "number"
          ? (members as any).size
          : ((members as any)?.toArray?.()?.length ?? 0);
      if (projectId && (!size || size === 0)) {
        (dispatch as any)(ProjectAction.getMembers(projectId));
      }
    } catch {
      if (projectId) {
        (dispatch as any)(ProjectAction.getMembers(projectId));
      }
    }
  }, [projectId, members, dispatch]);

  const [editorHtml, setEditorHtml] = useState("");
  const [editorText, setEditorText] = useState("");
  const [mentionedMembers, setMentionedMembers] = useState<any[]>([]);
  const editorRef = useRef<any>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [emojiMenuOpen, setEmojiMenuOpen] = useState(false);
  const [emojiMenuPosition, setEmojiMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const emojiButtonRef = useRef<HTMLButtonElement | null>(null);
  const emojiMenuRef = useRef<HTMLDivElement | null>(null);

  // Emoji menu for comment reactions
  const [commentEmojiMenuOpen, setCommentEmojiMenuOpen] = useState(false);
  const [commentEmojiMenuPosition, setCommentEmojiMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [commentEmojiMenuCommentId, setCommentEmojiMenuCommentId] = useState<string | null>(null);
  const commentEmojiMenuRef = useRef<HTMLDivElement | null>(null);

  // Track which comment user is replying to
  const [replyToCommentId, setReplyToCommentId] = useState<string | null>(null);

  // Filters
  const [filterOpen, setFilterOpen] = useState(false);
  const [showResolved, setShowResolved] = useState(true);
  const [onlyMyThreads, setOnlyMyThreads] = useState(false);
  const filterRef = useRef<HTMLDivElement | null>(null);

  // Collapse control similar to DealDetailPanel
  const MAX_SHOWN = 6;
  const [collapsed, setCollapsed] = useState(true);

  // Skeleton for comment loading state
  const CommentSkeleton = () => (
    <div className="flex gap-3">
      {/* Avatar skeleton */}
      <div className="flex-shrink-0">
        <div className="w-5 h-5 rounded-full bg-gray-200 animate-pulse" />
      </div>
      {/* Content skeleton */}
      <div className="flex-1 min-w-0 space-y-2">
        {/* Author and time skeleton */}
        <div className="flex items-center gap-2">
          <div className="h-4 bg-gray-200 rounded w-24 animate-pulse" />
          <div className="h-3 bg-gray-100 rounded w-16 animate-pulse" />
        </div>
        {/* Comment text skeleton */}
        <div className="space-y-1">
          <div className="h-4 bg-gray-100 rounded w-full animate-pulse" />
          <div className="h-4 bg-gray-100 rounded w-3/4 animate-pulse" />
        </div>
        {/* Actions skeleton */}
        <div className="flex items-center gap-2 mt-2">
          <div className="h-3 w-3 bg-gray-200 rounded animate-pulse" />
          <div className="h-3 w-3 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
    </div>
  );

  // Handle emoji click
  const handleEmojiClick = (emoji: string) => {
    try {
      const editor = editorRef.current;
      if (editor && editor.getModule) {
        const quill = editor;
        const range = quill.getSelection();
        const index = range ? range.index : quill.getLength() - 1;

        // Insert emoji at current cursor position
        quill.insertText(index, emoji);
        quill.setSelection(index + emoji.length);

        // Trigger onChange manually to update state
        const html = quill.root.innerHTML;
        const text = quill.getText();
        setEditorHtml(html);
        setEditorText(text);
      }
    } catch (error) {
      console.error("Error inserting emoji:", error);
    }
    setEmojiMenuOpen(false);
  };

  // Handle emoji button click
  const handleEmojiButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (emojiButtonRef.current) {
      const rect = emojiButtonRef.current.getBoundingClientRect();
      setEmojiMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
      setEmojiMenuOpen(!emojiMenuOpen);
    }
  };

  // Close emoji menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        emojiMenuRef.current &&
        !emojiMenuRef.current.contains(event.target as Node) &&
        emojiButtonRef.current &&
        !emojiButtonRef.current.contains(event.target as Node)
      ) {
        setEmojiMenuOpen(false);
      }
      if (
        commentEmojiMenuRef.current &&
        !commentEmojiMenuRef.current.contains(event.target as Node)
      ) {
        setCommentEmojiMenuOpen(false);
        setCommentEmojiMenuCommentId(null);
      }
    };

    if (emojiMenuOpen || commentEmojiMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [emojiMenuOpen, commentEmojiMenuOpen]);

  // Close filter menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setFilterOpen(false);
      }
    };

    if (filterOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [filterOpen]);

  // Handle reply to comment
  const handleReply = (comment: CommentItem) => {
    try {
      const editor = editorRef.current;
      if (editor && editor.getModule) {
        // Focus editor
        editor.focus();

        // Insert mention of comment author
        if (comment.authorId) {
          const mentionModule = editor.getModule("mention");
          if (mentionModule) {
            // Find member by accountId
            const member = editorMembers.find(
              (m: any) => String(m.get("accountId")) === String(comment.authorId)
            );
            if (member) {
              const range = editor.getSelection();
              const index = range ? range.index : editor.getLength();
              // Set cursor position for programmatic insert
              editor.setSelection(index, 0);
              // Set mentionModule cursor positions (required for insertItem)
              mentionModule.cursorPos = index;
              mentionModule.mentionCharPos = index;
              // Prepare mention data with correct format
              const mentionData = {
                id: member.get("accountId") || comment.authorId,
                value: member.get("displayName") || comment.author,
                type: "mention",
                denotationChar: "@",
              };
              // Use mentionModule.insertItem with programmaticInsert = true
              mentionModule.insertItem(mentionData, true);
            } else {
              // Fallback: just insert @author name as plain text
              const range = editor.getSelection();
              const index = range ? range.index : editor.getLength();
              editor.insertText(index, `@${comment.author} `);
              editor.setSelection(index + comment.author.length + 2);
            }
          }
        }
        setReplyToCommentId(comment.id);
      }
    } catch (error) {
      console.error("Error handling reply:", error);
    }
  };

  // Handle resolve/unresolve comment
  const handleResolve = async (comment: CommentItem, resolved: boolean) => {
    if (!itemId || !comment.id) return;
    // Find the log item to get activity id
    const log = itemActivities.find((l: any) => String(l.get("id")) === String(comment.id));
    if (!log) return;

    const activityId = log.get("id");
    await markAsDoneActivity(itemId, activityId, resolved);
    dispatch(
      ActivityAction.searchActivities({
        itemId,
        scope: "item",
        includeDependencies:
          itemType === "COMPANY" || itemType === "CONTACT" || itemType === "SALE",
      })
    );
    if (isGmailContext()) {
      dispatch(ActivityAction.searchUpcomingChrome(projectId, upcomingFilters));
    }
  };

  const parseReactions = (rawReactions: any): CommentReaction[] => {
    if (!rawReactions) return [];

    try {
      if (Array.isArray(rawReactions)) {
        // Check if it's an array of valid reaction objects
        const validObjects = rawReactions.filter(
          (r: any) => r && typeof r === "object" && typeof r.emoji === "string"
        );

        if (validObjects.length > 0) {
          // Normal array of objects
          return validObjects.map((r: any) => ({
            emoji: r.emoji,
            count: Number(r.count) || 0,
            userReacted: Boolean(r.userReacted),
          }));
        } else {
          // Handle string array format: ["[{emoji=\uD83D\uDE2D", " count=1", " userReacted=true}]"]
          const joinedString = rawReactions
            .filter((r: any) => typeof r === "string")
            .join("")
            .trim();

          if (joinedString) {
            try {
              // Try to extract object from string like "[{emoji=😭, count=1, userReacted=true}]"
              let jsonLike = joinedString
                .replace(/\[{/g, "[{")
                .replace(/}/g, "}")
                .replace(/=/g, ":")
                .replace(/(\w+):/g, '"$1":')
                .replace(/:(\w+)/g, ':"$1"')
                .replace(/:(\d+)/g, ":$1")
                .replace(/:true/g, ":true")
                .replace(/:false/g, ":false");

              const parsed = JSON.parse(jsonLike);
              if (Array.isArray(parsed) && parsed.length > 0) {
                return parsed
                  .filter((r: any) => r && typeof r === "object" && r.emoji)
                  .map((r: any) => ({
                    emoji: r.emoji,
                    count: Number(r.count) || 0,
                    userReacted: r.userReacted === true || r.userReacted === "true",
                  }));
              }
            } catch {
              // Fallback: extract emoji and properties manually
              const emojiMatch = joinedString.match(/emoji[=:]([^,\s}]+)/);
              const countMatch = joinedString.match(/count[=:]([^,\s}]+)/);
              const userReactedMatch = joinedString.match(/userReacted[=:]([^,\s}]+)/);

              if (emojiMatch) {
                return [
                  {
                    emoji: emojiMatch[1].trim().replace(/['"]/g, ""),
                    count: countMatch ? Number(countMatch[1].trim()) || 1 : 1,
                    userReacted: userReactedMatch ? userReactedMatch[1].trim() === "true" : false,
                  },
                ];
              }
            }
          }
        }
      } else if (typeof rawReactions === "string") {
        // Try to parse if it's a JSON string
        try {
          const parsed = JSON.parse(rawReactions);
          if (Array.isArray(parsed)) {
            return parsed
              .filter((r: any) => r && typeof r === "object" && r.emoji)
              .map((r: any) => ({
                emoji: r.emoji,
                count: Number(r.count) || 0,
                userReacted: Boolean(r.userReacted),
              }));
          }
        } catch {
          return [];
        }
      }
    } catch {
      return [];
    }

    return [];
  };

  const updateCommentReactions = async (
    activityId: string,
    updatedReactions: CommentReaction[],
    _successMessage: string,
    _errorMessage: string
  ) => {
    await updateTaskField(String(activityId), "reactions", updatedReactions, "ACTIVITY");
    dispatch(
      ActivityAction.searchActivities({
        itemId,
        scope: "item",
        includeDependencies:
          itemType === "COMPANY" || itemType === "CONTACT" || itemType === "SALE",
      })
    );
  };

  const getCommentReactions = (
    commentId: string
  ): { activityId: string; reactions: CommentReaction[] } | null => {
    if (!itemId) return null;

    const log = itemActivities.find((l: any) => String(l.get("id")) === String(commentId));
    if (!log) return null;

    const activityId = log.get("id");
    const currentData = log.getIn(["event", "data"]).toJS() || {};
    const currentReactions = parseReactions(currentData.reactions);

    return { activityId: String(activityId), reactions: currentReactions };
  };

  const handleToggleReaction = async (commentId: string, emoji: string) => {
    const commentData = getCommentReactions(commentId);
    if (!commentData) return;

    const { activityId, reactions: currentReactions } = commentData;
    const existingIndex = currentReactions.findIndex((r: CommentReaction) => r.emoji === emoji);
    let updatedReactions: CommentReaction[];

    if (existingIndex >= 0) {
      // Remove reaction (toggle off)
      const existing = currentReactions[existingIndex];
      if (existing.count === 1) {
        // Remove entire reaction if count is 1
        updatedReactions = currentReactions.filter((_, idx) => idx !== existingIndex);
      } else {
        // Decrease count and mark as not reacted
        updatedReactions = [...currentReactions];
        updatedReactions[existingIndex] = {
          ...existing,
          count: existing.count - 1,
          userReacted: false,
        };
      }
    } else {
      // Add reaction (toggle on)
      updatedReactions = [...currentReactions, { emoji, count: 1, userReacted: true }];
    }

    await updateCommentReactions(
      activityId,
      updatedReactions,
      existingIndex >= 0 ? t("Reaction removed") : t("Reaction added"),
      t("Cannot toggle reaction")
    );
  };

  const handleAddReactionToComment = async (commentId: string, emoji: string) => {
    const commentData = getCommentReactions(commentId);
    if (!commentData) return;

    const { activityId, reactions: currentReactions } = commentData;
    const existingIndex = currentReactions.findIndex((r: CommentReaction) => r.emoji === emoji);
    let updatedReactions: CommentReaction[];

    if (existingIndex >= 0) {
      // Update existing reaction: increment count if user hasn't reacted
      const existing = currentReactions[existingIndex];
      updatedReactions = [...currentReactions];
      updatedReactions[existingIndex] = {
        ...existing,
        count: existing.count + (existing.userReacted ? 0 : 1),
        userReacted: true,
      };
    } else {
      // Add new reaction
      updatedReactions = [...currentReactions, { emoji, count: 1, userReacted: true }];
    }

    await updateCommentReactions(
      activityId,
      updatedReactions,
      t("Reaction added"),
      t("Cannot add reaction")
    );
  };

  const handleAddReaction = (commentId: string, buttonRef: React.RefObject<HTMLButtonElement>) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCommentEmojiMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
      setCommentEmojiMenuCommentId(commentId);
      setCommentEmojiMenuOpen(!commentEmojiMenuOpen);
    }
  };

  const handleCommentEmojiClick = (emoji: string) => {
    if (commentEmojiMenuCommentId) {
      handleAddReactionToComment(commentEmojiMenuCommentId, emoji);
    }
    setCommentEmojiMenuOpen(false);
    setCommentEmojiMenuCommentId(null);
  };

  const handleInsertEmojiForComment = (
    comment: CommentItem,
    buttonRef: React.RefObject<HTMLButtonElement>
  ) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCommentEmojiMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
      setCommentEmojiMenuCommentId(comment.id);
      setCommentEmojiMenuOpen(!commentEmojiMenuOpen);
    }
  };

  // Handle mention button click
  const handleMention = (comment: CommentItem) => {
    try {
      const editor = editorRef.current;
      if (editor && editor.getModule) {
        const mentionModule = editor.getModule("mention");
        if (mentionModule) {
          // Find member by accountId
          const member = editorMembers.find(
            (m: any) => String(m.get("accountId")) === String(comment.authorId)
          );
          if (member) {
            const range = editor.getSelection();
            const index = range ? range.index : editor.getLength();
            // Set cursor position for programmatic insert
            editor.setSelection(index, 0);
            // Set mentionModule cursor positions (required for insertItem)
            mentionModule.cursorPos = index;
            mentionModule.mentionCharPos = index;
            // Prepare mention data with correct format
            const mentionData = {
              id: member.get("accountId") || comment.authorId,
              value: member.get("displayName") || comment.author,
              type: "mention",
              denotationChar: "@",
            };
            // Use mentionModule.insertItem with programmaticInsert = true
            mentionModule.insertItem(mentionData, true);
          } else {
            // Fallback: just insert @author name as plain text
            const range = editor.getSelection();
            const index = range ? range.index : editor.getLength();
            editor.insertText(index, `@${comment.author} `);
            editor.setSelection(index + comment.author.length + 2);
          }
        }
      }
    } catch (error) {
      console.error("Error handling mention:", error);
    }
  };

  // Handle show replies
  const handleShowReplies = (_commentId: string) => {
    // TODO: Implement expand/collapse replies when API is available
    // Replies are already rendered inline, this can be used for expand/collapse in future
  };

  const renderListComment = () => {
    const myId = currentUser?.get?.("accountId");
    // Filter only top-level comments (no parentId)
    const topLevelComments = commentItems.filter((c) => !c.parentId);
    const visibleItems = topLevelComments.filter((c) => {
      if (!showResolved && c.isResolved) return false;
      if (onlyMyThreads && myId && c.authorId && String(c.authorId) !== String(myId)) return false;
      return true;
    });

    const needCollapse = visibleItems.length > MAX_SHOWN;
    const visibleRows = collapsed && needCollapse ? visibleItems.slice(0, MAX_SHOWN) : visibleItems;

    if (visibleItems.length === 0) {
      if (isLoading) {
        return (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, idx) => (
              <CommentSkeleton key={`skeleton-${idx}`} />
            ))}
          </div>
        );
      }
      return <div className="text-xs text-gray-500 py-4 text-center">{t("No comments yet")}</div>;
    }

    return (
      <div className="space-y-4">
        {visibleRows.map((c, index) => (
          <CommentThread
            key={c.id}
            comment={c}
            isLast={index === visibleRows.length - 1}
            isReply={false}
            showResolved={showResolved}
            onDelete={(id: string) => {
              setDeleteId(id);
              setConfirmOpen(true);
            }}
            onReply={handleReply}
            onResolve={handleResolve}
            onInsertEmoji={handleInsertEmojiForComment}
            onToggleReaction={handleToggleReaction}
            onAddReaction={handleAddReaction}
            onMention={handleMention}
            onShowReplies={handleShowReplies}
          />
        ))}
        {isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 1 }).map((_, idx) => (
              <CommentSkeleton key={`skeleton-${idx}`} />
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  // Fallback: ensure editor has at least currentUser in member list so mention menu won't be empty
  const editorMembers = useMemo(() => {
    try {
      const size =
        typeof (members as any)?.size === "number"
          ? (members as any).size
          : ((members as any)?.toArray?.()?.length ?? 0);
      if (size && size > 0) return members;
    } catch {}
    const cu = currentUser;
    if (cu && cu.get) {
      return fromJS([
        {
          accountId: cu.get("accountId"),
          displayName: cu.get("displayName"),
          avatarUrl: cu.get("avatarUrl"),
        },
      ]);
    }
    return fromJS([]);
  }, [members, currentUser]);

  const commentItems: CommentItem[] = useMemo(() => {
    try {
      const allComments = itemActivities
        .filter((l: any) => l.getIn(["event", "action"]) === "comment")
        .map((l: any) => {
          const personId = l.getIn(["event", "person"]);
          const member = members.find((m: any) => m.get("accountId") === personId);
          const commentId = String(l.get("id") || Math.random());
          const parentId = l.getIn(["event", "data", "parentId"]);
          const content =
            l.getIn(["event", "data", "description"]) ||
            l.getIn(["event", "data", "summary"]) ||
            "";
          return {
            id: commentId,
            author: member?.get?.("displayName") || t("Unknown"),
            authorId: personId ? String(personId) : undefined,
            avatarUrl: member?.get?.("avatarUrl") || "",
            createdAt: l.getIn(["event", "createdAt"]),
            content,
            replies: l.getIn(["event", "data", "repliesCount"]) || 0,
            parentId: parentId ? String(parentId) : undefined,
            isResolved: l.getIn(["event", "data", "done"]),
            resolvedBy: l.getIn(["event", "data", "resolvedBy"]) || undefined,
            reactions: parseReactions(l.getIn(["event", "data", "reactions"])?.toJS?.()),
            children: [],
          } as CommentItem;
        });

      // Group replies under their parent comments
      const commentMap = new Map<string, CommentItem>();
      const topLevelComments: CommentItem[] = [];

      // First pass: create map of all comments
      allComments.forEach((comment) => {
        commentMap.set(comment.id, { ...comment, children: [] });
      });

      // Second pass: group replies under parents
      allComments.forEach((comment) => {
        const commentWithChildren = commentMap.get(comment.id)!;
        if (comment.parentId && commentMap.has(comment.parentId)) {
          // This is a reply, add it to parent's children
          const parent = commentMap.get(comment.parentId)!;
          parent.children = parent.children || [];
          parent.children.push(commentWithChildren);
        } else {
          // This is a top-level comment
          topLevelComments.push(commentWithChildren);
        }
      });

      // Sort top-level comments and their replies by createdAt
      const sortComments = (comments: CommentItem[]): CommentItem[] => {
        return comments
          .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
          .map((comment) => ({
            ...comment,
            children: comment.children ? sortComments(comment.children) : [],
          }));
      };

      return sortComments(topLevelComments);
    } catch {
      return [];
    }
  }, [itemActivities, members, t]);

  const headerActions = (
    <div className="relative" ref={filterRef as any}>
      <button
        type="button"
        onClick={() => setFilterOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-1.5 py-0.5 h-[18px]"
      >
        {/* Filter text */}
        <span className="text-[10px] font-medium leading-4 text-gray-900">{t("Filter")}</span>
        {/* Chevron right */}
        <ArrowRightIcon className="w-2.5 h-2.5 text-gray-900" />
      </button>
      {filterOpen && (
        <div className="absolute right-0 mt-1 w-64 rounded-md border border-gray-200 bg-white shadow-xl z-[1000] p-2">
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50"
            onClick={() => setShowResolved((v) => !v)}
          >
            {showResolved ? (
              <CheckCircle className="w-4 h-4 text-blue-600" />
            ) : (
              <Circle className="w-4 h-4 text-gray-400" />
            )}
            <span className="text-sm text-gray-800">{t("Show resolved comments")}</span>
          </button>
          <button
            type="button"
            className="mt-1 flex w-full items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50"
            onClick={() => setOnlyMyThreads((v) => !v)}
          >
            {onlyMyThreads ? (
              <CheckCircle className="w-4 h-4 text-blue-600" />
            ) : (
              <Circle className="w-4 h-4 text-gray-400" />
            )}
            <span className="text-sm text-gray-800">{t("Only my threads")}</span>
          </button>
        </div>
      )}
    </div>
  );

  // Calculate needCollapse based on top-level comments
  const topLevelComments = commentItems.filter((c) => !c.parentId);
  const needCollapse = topLevelComments.length > MAX_SHOWN;
  const commentsCount = topLevelComments.length;

  const footer = needCollapse ? (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="inline-flex items-center gap-1 text-[10px] leading-4 text-gray-500 hover:text-gray-700"
      >
        <span>{collapsed ? t("Expand") : t("Collapse")}</span>
        {collapsed ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
      </button>
    </div>
  ) : null;

  return (
    <>
      <CollapsibleCard
        title={
          <div className="flex items-center gap-2">
            <span>{t("All comments")}</span>
            {commentsCount > 0 && (
              <span className="flex flex-row items-center justify-center w-[18px] h-[18px] rounded-xl flex-none flex-grow-0 text-xs font-medium text-gray-700 bg-gray-100">
                {commentsCount}
              </span>
            )}
          </div>
        }
        headerActions={headerActions}
        defaultCollapsed={false}
        bodyClassName="space-y-4"
        footer={footer}
        cardProps={{
          bgClass: "bg-white",
          borderClass: "border border-gray-200",
          roundedClass: "rounded-[12px]",
          shadow: "custom",
          shadowClass: "shadow-[0_1px_1px_0_rgba(0,0,0,0.05)]",
        }}
      >
        {/* List */}
        {renderListComment()}

        {/* Add comment */}
        <div className="mt-4 mb-5">
          <div className="bg-white border border-gray-200 rounded-[8px] p-2 flex flex-col gap-1">
            {/* Top section with avatar and input */}
            <div className="flex items-center justify-between w-full">
              {/* Avatar */}
              <div className="flex-shrink-0">
                {currentAvatarUrl ? (
                  <img
                    src={String(currentAvatarUrl)}
                    alt="You"
                    className="h-5 w-5 rounded-full object-cover"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = "none";
                    }}
                  />
                ) : (
                  <DefaultAvatar
                    name={currentUser?.get?.("displayName") || ""}
                    size="sm"
                    className="h-5 w-5 rounded-full bg-gray-300 text-white"
                  />
                )}
              </div>
              {/* Spacer */}
              <div className="h-full shrink-0 w-1" />
              {/* Input field with placeholder */}
              <div className="flex-1 min-w-0">
                <CommentInput
                  placeholder={t("Add comment...") as string}
                  editorContent={{ htmlContent: editorHtml, contents: {} }}
                  members={editorMembers}
                  editorInputRef={editorRef}
                  minRows={1}
                  showToolbar={false}
                  hideBorder={true}
                  onChange={(content: any) => {
                    setEditorHtml(content.htmlContent || "");
                    setEditorText(content.textContent || "");
                    try {
                      setMentionedMembers(content.mentionedMembers || []);
                    } catch {
                      setMentionedMembers([]);
                    }
                  }}
                  containerClassName="w-full"
                  editorClassName="comment-input-custom px-4"
                />
              </div>
            </div>
            {/* Bottom section with action icons and send button */}
            <div className="flex items-end justify-between w-full">
              {/* Action icons at bottom-left */}
              <div className="flex items-center gap-2 relative">
                <button
                  ref={emojiButtonRef}
                  type="button"
                  className="p-0 hover:opacity-70 transition-opacity"
                  title={t("Insert emoji") as string}
                  onClick={handleEmojiButtonClick}
                >
                  <SmilePlus className="w-3 h-3 text-gray-500" />
                </button>
                {emojiMenuOpen && emojiMenuPosition && (
                  <div
                    ref={emojiMenuRef}
                    className="fixed w-[280px] max-h-[240px] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-2"
                    style={{
                      top: `${emojiMenuPosition.top}px`,
                      left: `${emojiMenuPosition.left}px`,
                    }}
                  >
                    <div className="grid grid-cols-8 gap-1">
                      {commonEmojis.map((emoji, index) => (
                        <button
                          key={index}
                          type="button"
                          className="w-8 h-8 flex items-center justify-center text-lg hover:bg-gray-100 rounded transition-colors"
                          onClick={() => handleEmojiClick(emoji)}
                          title={emoji}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  className="p-0 hover:opacity-70 transition-opacity"
                  title={t("Mention with @") as string}
                  onClick={() => {
                    try {
                      const m = editorRef.current?.getModule?.("mention");
                      m?.openMenu?.("@");
                    } catch {}
                  }}
                >
                  <AtSign className="w-3 h-3 text-gray-500" />
                </button>
              </div>
              {/* Send button at bottom-right - rounded button with blue background, white icon */}
              <button
                type="button"
                onClick={async () => {
                  const text = (editorText || "").trim();
                  const html = (editorHtml || "").trim();
                  if (!text && !html) return;
                  if (!itemId) {
                    (dispatch as any)(
                      LayoutAction.setNotification({
                        title: t("Activity"),
                        type: "error",
                        message: t("Missing item id"),
                      })
                    );
                    return;
                  }
                  try {
                    const person = currentUser?.get?.("accountId");
                    const request: any = {
                      type: ActivityTypes.comment,
                      data: {
                        summary: text || t("Note"),
                        description: html || text,
                        activityType: ActivityTypes.comment,
                        item: {
                          value: itemId,
                          label: itemLabel || itemId,
                          type: itemType,
                        },
                        parentId: replyToCommentId || undefined,
                        reactions: [],
                        timeZone: getUserTimeZone(),
                      },
                      action: "comment",
                      mentionedMembers,
                      person,
                      // Backend uses this to create REPLY notification for parent comment author
                      ...(replyToCommentId ? { parentActivityId: replyToCommentId } : {}),
                    };
                    if (typeof (dispatch as any) === "function") {
                      await (dispatch as any)(
                        ActivityAction.createActivity(itemId as any, request)
                      );
                    } else {
                      await createItemLogActivity(itemId as any, request);
                    }
                    dispatch(
                      ActivityAction.searchActivities({
                        itemId,
                        scope: "item",
                        includeDependencies:
                          itemType === "COMPANY" || itemType === "CONTACT" || itemType === "SALE",
                      })
                    );
                    (dispatch as any)(
                      LayoutAction.setNotification({
                        title: t("Activity"),
                        type: "success",
                        message: t("Added note"),
                      })
                    );
                    // clear editor
                    try {
                      if (editorRef.current) {
                        // Clear Quill editor content
                        const quill = editorRef.current;
                        if (quill.setContents) {
                          quill.setContents([]);
                        } else if (quill.setText) {
                          quill.setText("");
                        }
                        // Also clear root HTML
                        if (quill.root) {
                          quill.root.innerHTML = "";
                        }
                      }
                    } catch {}
                    setEditorHtml("");
                    setEditorText("");
                    setMentionedMembers([]);
                    setReplyToCommentId(null);
                  } catch (error: any) {
                    (dispatch as any)(
                      LayoutAction.setNotification({
                        title: t("Activity"),
                        type: "error",
                        message:
                          error?.response?.data?.message ||
                          error?.message ||
                          t("Cannot create activity"),
                      })
                    );
                  }
                }}
                className="inline-flex items-center justify-center rounded-[6px] bg-blue-600 px-1.5 py-1 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
                aria-label={t("Send") as string}
                disabled={!(editorText || editorHtml)?.trim?.()}
              >
                <SendIcon className="w-3.5 h-3.5 text-white" />
              </button>
            </div>
          </div>
        </div>
      </CollapsibleCard>

      {/* Comment Emoji Menu */}
      {commentEmojiMenuOpen && commentEmojiMenuPosition && (
        <div
          ref={commentEmojiMenuRef}
          className="fixed w-[280px] max-h-[240px] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-2"
          style={{
            top: `${commentEmojiMenuPosition.top}px`,
            left: `${commentEmojiMenuPosition.left}px`,
          }}
        >
          <div className="grid grid-cols-8 gap-1">
            {commonEmojis.map((emoji, index) => (
              <button
                key={index}
                type="button"
                className="w-8 h-8 flex items-center justify-center text-lg hover:bg-gray-100 rounded transition-colors"
                onClick={() => handleCommentEmojiClick(emoji)}
                title={emoji}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      <ConfirmDialog
        visible={confirmOpen}
        title={t("Delete comment?") as string}
        description={t("This action cannot be undone.") as string}
        alertIcon={<OctagonAlertIcon />}
        dangerBtn
        submitting={deleting}
        onClose={() => {
          if (!deleting) {
            setConfirmOpen(false);
            setDeleteId(null);
          }
        }}
        onSubmit={async () => {
          if (!itemId || !deleteId) return;
          try {
            setDeleting(true);
            await deleteItemLog(itemId, deleteId);
            dispatch(
              ActivityAction.searchActivities({
                itemId,
                scope: "item",
                includeDependencies:
                  itemType === "COMPANY" || itemType === "CONTACT" || itemType === "SALE",
              })
            );
            setConfirmOpen(false);
            setDeleteId(null);
            dispatch(
              LayoutAction.setNotification({
                title: t("Activity"),
                type: "success",
                message: t("Deleted comment"),
              })
            );
          } catch (error: any) {
            dispatch(
              LayoutAction.setNotification({
                title: t("Activity"),
                type: "error",
                message:
                  error?.response?.data?.message || error?.message || t("Cannot delete comment"),
              })
            );
          } finally {
            setDeleting(false);
          }
        }}
        submitBtnLabel={t("Delete") as string}
      />
    </>
  );
};

export default CommentsPanel;
